/**
 * Registro ÚNICO de slot: nome + descrição + contrato de dados, num lugar só.
 *
 * Antes cada domínio dizia a mesma coisa em três lugares — a string em
 * `slots.X`, a descrição runtime em `slotContext["x"]` (strings "number?") e a
 * interface TS à mão em `slotContext.ts`. Nada garantia que batessem. Agora o
 * domínio declara um registro por slot (`defineSlotRegistry`) e as três formas antigas
 * são DERIVADAS dele: `slots` (nomes), `slotContext` (legado, mesmo formato) e
 * o tipo do context (`Infer`). Plugin e frontend não mudam uma linha.
 *
 * O schema `t` é de propósito mínimo — não valida nada em runtime; ele só
 * carrega o tipo TS e a descrição legível que a tooling imprime.
 */

// ─── descritores ────────────────────────────────────────────────────────────

export type DescKind = "string" | "number" | "boolean" | "any" | "object" | "array" | "record" | "typed";

/** Descritor de um campo. `T` é o tipo TS; `O` diz se o campo pode faltar. */
export interface Desc<T = unknown, O extends boolean = false> {
  readonly kind: DescKind;
  readonly optional: O;
  /** Só em `object`. */
  readonly fields?: Shape;
  /** Só em `array` e `record`. */
  readonly item?: Desc<any, boolean>;
  /** Só em `typed`: nome do tipo TS que o campo carrega (para a tooling). */
  readonly label?: string;
  /** Comentário curto mostrado pela tooling ("ISO", "filtre por aqui"). */
  readonly note?: string;
  /** Fantasma — nunca existe em runtime. */
  readonly __t?: T;
}

export type Shape = Readonly<Record<string, Desc<any, boolean>>>;

type OptionalKeys<S extends Shape> = { [K in keyof S]: S[K] extends Desc<any, true> ? K : never }[keyof S];
type RequiredKeys<S extends Shape> = Exclude<keyof S, OptionalKeys<S>>;
type Simplify<T> = { [K in keyof T]: T[K] } & {};

/** Tipo TS de um `Shape` (objeto de descritores). */
export type InferShape<S extends Shape> = Simplify<
  { [K in RequiredKeys<S>]: NonNullable<S[K]["__t"]> } &
  { [K in OptionalKeys<S>]?: NonNullable<S[K]["__t"]> }
>;

/** Tipo TS de um descritor. */
export type Infer<D> = D extends Desc<infer T, any> ? T : never;

function desc<T>(kind: DescKind, extra: Partial<Desc<T>> = {}): Desc<T, false> {
  return { kind, optional: false, ...extra } as Desc<T, false>;
}

/** Vocabulário do contrato. Um campo por chamada; `t.optional` quando pode faltar. */
export const t = {
  string: desc<string>("string"),
  number: desc<number>("number"),
  boolean: desc<boolean>("boolean"),
  /** Escape para o que o call-site passa sem forma fixa. Evite. */
  any: desc<any>("any"),
  /** Objeto com campos conhecidos — a tooling imprime cada um. */
  object<S extends Shape>(fields: S): Desc<InferShape<S>, false> {
    return desc<InferShape<S>>("object", { fields });
  },
  array<T>(item: Desc<T, boolean>): Desc<T[], false> {
    return desc<T[]>("array", { item });
  },
  /** `{ [chave: string]: item }`. */
  record<T>(item: Desc<T, boolean>): Desc<Record<string, T>, false> {
    return desc<Record<string, T>>("record", { item });
  },
  /** Tipo TS que já existe (`IPluginProvides`, `TemaDoApp`) — a tooling mostra o `label`. */
  typed<T>(label: string): Desc<T, false> {
    return desc<T>("typed", { label });
  },
  optional<T>(d: Desc<T, boolean>): Desc<T, true> {
    return { ...d, optional: true } as Desc<T, true>;
  },
  /** Mesmo descritor com um comentário para a tooling. */
  note<T, O extends boolean>(d: Desc<T, O>, note: string): Desc<T, O> {
    return { ...d, note };
  },
} as const;

// ─── registro ───────────────────────────────────────────────────────────────

export interface SlotDefinition<N extends string = string, S extends Shape = Shape> {
  /** A string do slot — o contrato com o frontend. Nunca mude uma publicada. */
  readonly name: N;
  /** Onde aparece na tela e para que serve, em uma ou duas frases. */
  readonly description: string;
  /** O que `slot.context` entrega. `{}` = nada (leia do `slot.runtime`). */
  readonly context: S;
  /** Declarado no catálogo, mas nenhum ponto do app o monta hoje. */
  readonly unmounted?: boolean;
}

export type SlotRegistry = Readonly<Record<string, SlotDefinition<string, Shape>>>;

/** Identidade tipada: fixa os literais (`name`) para as derivações. */
export function defineSlotRegistry<
  N extends string,
  S extends Shape,
  R extends Record<string, SlotDefinition<N, S>>
>(registry: R): R {
  return registry;
}

/** `{ ACTIONS: "ticket-actions", ... }` — o `slots` de sempre, derivado. */
export type SlotNamesOf<R extends SlotRegistry> = { readonly [K in keyof R]: R[K]["name"] };

export function slotNamesOf<R extends SlotRegistry>(registry: R): SlotNamesOf<R> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(registry)) out[key] = registry[key].name;
  return out as SlotNamesOf<R>;
}

/** Mapa nome-do-slot → tipo do context, derivado do registro. */
export type SlotContextMapOf<R extends SlotRegistry> = {
  [K in keyof R as R[K]["name"]]: InferShape<R[K]["context"]>;
};

/**
 * Formato LEGADO de `slotContext` (`{ campo: "number?" }`), derivado do
 * registro. Objetos aninhados viram "object" — quem quer o detalhe usa
 * `describeShape`. Mantido para quem já lê `dominio.slotContext`.
 */
export type LegacySlotContextOf<R extends SlotRegistry> = {
  readonly [K in keyof R as R[K]["name"]]: Readonly<Record<string, string>>;
};

export function legacySlotContextOf<R extends SlotRegistry>(registry: R): LegacySlotContextOf<R> {
  const out: Record<string, Record<string, string>> = {};
  for (const key of Object.keys(registry)) {
    const def = registry[key];
    const shape: Record<string, string> = {};
    for (const [field, d] of Object.entries(def.context)) shape[field] = legacyKind(d) + (d.optional ? "?" : "");
    out[def.name] = shape;
  }
  return out as LegacySlotContextOf<R>;
}

function legacyKind(d: Desc<any, boolean>): string {
  switch (d.kind) {
    case "string": case "number": case "boolean": return d.kind;
    case "array": return legacyKind(d.item!) + "[]";
    default: return "object";
  }
}

// ─── descrição legível (tooling) ────────────────────────────────────────────

/** `queue?: { id: number, name: string, color?: string }` — para docs e CLI. */
export function describeDesc(d: Desc<any, boolean>): string {
  switch (d.kind) {
    case "object": return "{ " + describeShape(d.fields ?? {}) + " }";
    case "array": return describeDesc(d.item!) + "[]";
    case "record": return "{ [key: string]: " + describeDesc(d.item!) + " }";
    case "typed": return d.label ?? "unknown";
    default: return d.kind;
  }
}

export function describeShape(shape: Shape): string {
  return Object.entries(shape)
    .map(([k, d]) => `${k}${d.optional ? "?" : ""}: ${describeDesc(d)}`)
    .join(", ");
}

/** Uma linha por campo, com indentação para objetos aninhados. */
export function describeShapeLines(shape: Shape, indent = "  "): string[] {
  const lines: string[] = [];
  for (const [k, d] of Object.entries(shape)) {
    const head = `${indent}${(k + (d.optional ? "?" : "")).padEnd(22)} `;
    if (d.kind === "object" && d.fields) {
      lines.push(head + "{" + (d.note ? `   // ${d.note}` : ""));
      lines.push(...describeShapeLines(d.fields, indent + "  "));
      lines.push(indent + "}");
    } else {
      lines.push(head + describeDesc(d) + (d.note ? `   // ${d.note}` : ""));
    }
  }
  return lines;
}
